import React, { useRef } from 'react';
import { useAtomValue } from 'jotai';
import { ExpandIcon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { uiContextAtomTileActions } from '@rocco/ui/tile-shared/hooks/use-shared-tiles';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { StaticTileViewModel } from '@rocco/ui/tile-static/view-models/static';
import { OptionalLink } from '@rocco/components/link/OptionalLink';
import { Button } from '@rocco/components/button';
import { TILE_COMPONENTS_CLASS_NAMES } from '@rocco/ui/tile-shared/utils/contstants';

export const StaticTile = (props: TileViewModel<StaticTileViewModel>) => {
    const {
        aspectRatio,
        model,
        inlineActionButtons,
        onClick,
        disableLink = false,
        onExpandButtonClick,
        className,
    } = props;
    const isImage = model.type === 'photo';

    const actionsContext = useAtomValue(uiContextAtomTileActions);
    const { onStaticTileClick } = actionsContext?.trackingConfig ?? {};
    const tileRef = useRef<HTMLAnchorElement>(null);

    const handleTileClick = (
        e:
            | React.MouseEvent<HTMLDivElement>
            | React.MouseEvent<HTMLAnchorElement>
    ) => {
        if (onStaticTileClick || onClick) {
            e.preventDefault();
            e.stopPropagation();
            onClick?.();
            onStaticTileClick?.(model.title);
        }
    };

    return (
        <OptionalLink
            ref={tileRef}
            mode={disableLink ? 'override' : 'normal'}
            to={model.link ?? ''}
            onClick={handleTileClick}
        >
            <div
                className={cn(
                    'group flex h-full flex-col gap-[1.125rem] md:gap-[1.2rem]',
                    className
                )}
            >
                <div className="group relative h-full overflow-hidden rounded-2.5">
                    <div className="flex flex-col gap-4">
                        <div
                            className={cn(
                                'rocco-tile-media',
                                mapAspectRatio(aspectRatio),
                                'relative flex items-center justify-center overflow-hidden rounded-tile bg-gray-300'
                            )}
                        >
                            <div
                                className={cn(
                                    isImage &&
                                        'flex h-[84%] w-[84%] items-center justify-center'
                                )}
                            >
                                <img
                                    src={model.thumbnail.src}
                                    alt={model.thumbnail.alt}
                                    className={cn(
                                        isImage &&
                                            'max-h-full max-w-full bg-white object-contain p-3 shadow-md'
                                    )}
                                />
                            </div>
                            {model.fileExtension && (
                                <div className="bg-charcoal-400 absolute bottom-[33.81%] left-[34%] z-10 w-[15%] rounded-[2.4px] px-0.5 py-0 text-center text-[0.92rem] font-normal uppercase leading-tight text-white">
                                    {model.fileExtension}
                                </div>
                            )}
                            {onExpandButtonClick && (
                                <div className="absolute right-4 top-3.5 flex gap-1 transition-opacity duration-300">
                                    <Button
                                        type="button"
                                        size="square42"
                                        color="secondary70"
                                        onClick={(e) => {
                                            // prevent from firing route event
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onExpandButtonClick?.();
                                        }}
                                        aria-label={
                                            model.type === 'photo'
                                                ? 'Expand photo'
                                                : 'View file'
                                        }
                                    >
                                        <ExpandIcon className={cn('size-4')} />
                                    </Button>
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col gap-4">
                            {inlineActionButtons && (
                                <div className="flex flex-col">
                                    {inlineActionButtons}
                                </div>
                            )}
                            <div
                                className={`${TILE_COMPONENTS_CLASS_NAMES.TILE_DESCRIPTION_BLOCK} flex flex-col gap-[0.125rem]`}
                            >
                                <span className="line-clamp-1 overflow-hidden text-ellipsis text-sm font-medium tracking-[0.03rem] md:text-base md:tracking-[0.02rem]">
                                    {model.title}
                                </span>
                                {model.description && (
                                    <span className="overflow-hidden text-ellipsis text-nowrap text-sm font-medium tracking-[0.03rem] text-gray-900/40 md:text-base md:tracking-[0.02rem]">
                                        {model.description}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </OptionalLink>
    );
};

const mapAspectRatio = (
    aspectRatio: TileViewModel<StaticTileViewModel>['aspectRatio']
): string => {
    const configs: Record<string, string> = {
        featured: 'h-full', // there's no aspect ratio for featured tile - it should take all available space in the grid
        default: 'aspect-tile-default',
        article: 'aspect-tile-article',
        category: 'aspect-tile-category',
        square: 'aspect-square',
    };

    return configs[aspectRatio ?? 'default'] ?? 'aspect-tile-default';
};
